import LS from "../utils/localStorageUtils";
import { axiosInstance } from "./axiosInstance";
import type { AxiosResponse, ResponseType as AxiosResponseType } from "axios";
import { env } from "./env";

type HttpMethod = "get" | "post" | "put" | "patch" | "delete";
type Headers = Record<string, string>;

export type GetLobbyRoomsOpts = { start?: number; limit?: number };

class APIRequestor {
  // 🔹 declare fields
  private static instance: APIRequestor;
  private API_URL: string;
  private AUTH_API_URL: string;
  private FILE_API_URL: string;
  private BOWA_API_URL: string;
  private PAYMENT_API_URL: string;

  constructor() {
    // this.API_URL = process.env?.PORTAL_API_URL || "api";
    this.API_URL = env("SVARA_API_URL") as string;
    this.AUTH_API_URL = this.API_URL + "/api/svara/auth";
    this.FILE_API_URL = this.API_URL + "/api/file-storage";

    this.BOWA_API_URL = env("BOWA_API_URL") as string;
    this.PAYMENT_API_URL = this.BOWA_API_URL;
  }

  static getInstance() {
    if (!APIRequestor.instance) APIRequestor.instance = new APIRequestor();
    return APIRequestor.instance;
  }

  bowaRequest(
    endpoint: string,
    method: HttpMethod,
    data?: any,
    headers: Headers = {}
  ) {
    const url = this.PAYMENT_API_URL + endpoint;
    return this.requestServer(url, method, data, {
      ...this.getBearerHeader(),
      ...headers,
    });
  }

  getXCasinoHeader(): Headers {
    const casinoId = env("CASINO_ID");
    return { "X-Casino-Context": JSON.stringify({ casinoId }) };
  }
  // getXSocialCasinoHeader(): Headers {
  //   const casinoId = "casino-0ac1bb6d-a473-42a7-b5e0-5dbc87241122";
  //   return { "X-Casino-Context": JSON.stringify({ casinoId }) };
  // }

  getBearerHeader(): Headers {
    const token = LS.get("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  getApiUrl() {
    return this.API_URL;
  }

  composeFileUrl(fileId: string) {
    return `${this.FILE_API_URL}/${fileId}`;
  }

  request(
    endpoint: string,
    method: HttpMethod,
    data?: any,
    responseType?: AxiosResponseType
  ) {
    const url = this.API_URL + endpoint;
    return this.requestServer(
      url,
      method,
      data,
      this.getBearerHeader(),
      responseType
    );
  }

  authRequest(
    endpoint: string,
    method: HttpMethod,
    data?: any,
    headers: Headers = {}
  ) {
    const url = this.AUTH_API_URL + endpoint;
    return this.requestServer(url, method, data, headers);
  }

  freeRequest(
    endpoint: string,
    method: HttpMethod,
    data?: any,
    headers: Headers = {}
  ) {
    const url = this.API_URL + endpoint;
    return this.requestServer(url, method, data, headers);
  }

  fileRequest(fileId: string, responseType?: AxiosResponseType) {
    const url = `${this.FILE_API_URL}/${fileId}`;
    return this.requestServer(url, "get", undefined, {}, responseType);
  }

  privateRequest(
    endpoint: string,
    method: HttpMethod,
    token: string,
    data?: any
  ) {
    const url = this.API_URL + endpoint;
    return this.requestServer(url, method, data, {
      Authorization: `Bearer ${token}`,
    });
  }

  private requestServer(
    url: string,
    method: HttpMethod,
    data?: any,
    headers: Headers = {},
    responseType?: AxiosResponseType
  ): Promise<{ Data: any; Headers: any; Error?: any }> {
    return new Promise((resolve) => {
      console.log(`[API] Making ${method.toUpperCase()} request to:`, url);
      console.log(`[API] Headers:`, headers);
      console.log(`[API] Data:`, data);
      
      axiosInstance({
        url,
        method,
        data,
        headers,
        responseType,
      })
        .then((response: AxiosResponse) => {
          console.log(`[API] Success response from ${url}:`, {
            status: response.status,
            dataKeys: Object.keys(response?.data ?? {}),
          });
          resolve({
            Data: response?.data ?? {},
            Headers: response?.headers ?? {},
          });
        })
        .catch((error) => {
          console.error(`[API] Error from ${url}:`, {
            message: error?.message,
            code: error?.code,
            response: error?.response ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            } : null,
            request: error?.request ? {
              url: error.request.url,
              method: error.request.method,
            } : null,
            stack: error?.stack,
          });
          resolve({
            Data: {},
            Error: error,
            Headers: {},
          });
        });
    });
  }
}

const API = APIRequestor.getInstance();
export default API;
